import React from 'react'
import { Link } from 'react-router-dom'

import { routes } from 'constants/routes'

// Components
import Button from 'components/Button'
import Banner from 'components/Banner'
import Section from 'components/Section'
import { pluralize } from 'components/Pluralize'

import { useModalContext } from 'containers/ModalManager/ModalContext'

// Local Components
import {
  MembershipsPanel,
  MemberActions,
  WorkspaceSettingsSubscribePanel,
  WorkspaceSettingsDetailsPanel,
} from 'modules/workspace/components'

import {
  DeleteWorkspaceModal,
  AddSeatsModal,
  InviteMembersNotAllowedModal,
  InviteMembersModal,
} from 'modules/workspace/modals'

import {
  isWorkspaceSubscriptionActive,
  isWorkspaceSubscriptionTrialling,
  hasPaymentDetails,
} from 'modules/workspace/utils'

import dateFormat from 'utils/localizedDateFormat'

// Usage of BillingStatus enum is needed to make the expected strings match the BE ones
// eslint-disable-next-line no-restricted-imports
import { BillingStatus } from 'gqlTypes/expansiveTypes'
import {
  BillingPlanFragment,
  BillingSeatsInfoFragment,
  PaymentDetailsFragment,
} from 'gqlTypes'

import { Paragraph } from './WorkspaceSettingsGeneral.styles'

interface WorkspaceSettingsGeneralProps {
  workspaceId: string
  name: string
  avatar?: string
  seats: BillingSeatsInfoFragment
  isUserAdministrator: boolean
  billingStatus: BillingStatus
  nextBillingCycleDate?: string
  customerId: string
  currentPlan?: BillingPlanFragment
  paymentDetails?: PaymentDetailsFragment
  isSsoEnabled: boolean
  subscriptionEnd?: string
  isFinanceRole?: boolean
}

const WorkspaceSettingsGeneral: React.FC<WorkspaceSettingsGeneralProps> = props => {
  const {
    avatar,
    workspaceId,
    customerId,
    name,
    isUserAdministrator,
    seats,
    currentPlan,
    nextBillingCycleDate,
    billingStatus,
    paymentDetails,
    isSsoEnabled,
    subscriptionEnd,
    isFinanceRole,
  } = props

  const { showModal, showRestrictedModal } = useModalContext()
  const scheduledSeats = seats.scheduledSeatsTotal ?? undefined

  const isOnTrial = isWorkspaceSubscriptionTrialling(billingStatus)
  const isSubscriptionActive = isWorkspaceSubscriptionActive(billingStatus)
  const customerHasPaymentDetails = hasPaymentDetails(paymentDetails)

  const onInviteMember = () => {
    if (isSsoEnabled) {
      showModal(InviteMembersNotAllowedModal, { workspaceId })
    } else {
      showRestrictedModal(InviteMembersModal, {
        customerId,
        isOnTrial,
        nextBillingCycleDate: nextBillingCycleDate!,
        plan: currentPlan!,
        seats,
        workspaceId,
        paymentDetails: paymentDetails!,
        isScheduledToBeCancelled: !!subscriptionEnd,
      })
    }
  }

  const getMembersTabCopy = () => {
    const availableSeatsCopy = `of which ${seats.availableSeats} ${pluralize(
      'is',
      'are',
      seats.availableSeats
    )} not in use`
    const editorSeatsCountCopy = `${seats.currentSeatsTotal} Editor
    ${pluralize('Seat', 'Seats', seats.currentSeatsTotal)}`

    if (!isUserAdministrator && !isFinanceRole) {
      return
    }

    if (isOnTrial) {
      return (
        <Paragraph data-testid="members-tab-copy">
          After your trial, you will begin paying for{' '}
          <b>{editorSeatsCountCopy}</b>
          {seats.availableSeats > 0 ? availableSeatsCopy : null}
        </Paragraph>
      )
    } else {
      return (
        <Paragraph data-testid="members-tab-copy">
          You currently pay for <b>{editorSeatsCountCopy}</b>, <br />
          {seats.availableSeats > 0 ? availableSeatsCopy : null}
        </Paragraph>
      )
    }
  }

  return (
    <>
      {isUserAdministrator && !customerHasPaymentDetails && (
        <WorkspaceSettingsSubscribePanel workspaceId={workspaceId} />
      )}

      {!isFinanceRole && (
        <Section title="Workspace">
          <WorkspaceSettingsDetailsPanel
            name={name}
            id={workspaceId}
            avatar={avatar}
            isUserAdministrator={isUserAdministrator}
          />
        </Section>
      )}

      <Section
        title={`${seats.currentSeatsTotal} Members in Workspace`}
        text={getMembersTabCopy()}
        action={
          isSubscriptionActive &&
          (isUserAdministrator || isFinanceRole) && (
            <>
              <Button
                onClick={() =>
                  showModal(AddSeatsModal, {
                    seats,
                    plan: currentPlan!,
                    workspaceId,
                    nextBillingCycleDate,
                    customerId,
                    isOnTrial,
                    paymentDetails: paymentDetails,
                    isScheduledToBeCancelled: !!subscriptionEnd,
                  })
                }
                small
                variant="secondary">
                Add Seats…
              </Button>
              {!isFinanceRole && (
                <Button
                  onClick={() => onInviteMember()}
                  small
                  variant="primary">
                  Invite Members…
                </Button>
              )}
            </>
          )
        }>
        {/* Banner warning the user that has scheduled seats */}
        {scheduledSeats && isUserAdministrator && (
          <>
            <br />
            <Banner type="warning">
              <b>Scheduled change!</b> You’ve scheduled to have {scheduledSeats}{' '}
              seats starting on{' '}
              {nextBillingCycleDate &&
                dateFormat(new Date(nextBillingCycleDate))}
              {'. '}
              <Link
                to={routes.WORKSPACE_SETTINGS_BILLING.create({
                  workspaceId: workspaceId,
                })}>
                See Details
              </Link>
            </Banner>
          </>
        )}

        {!isFinanceRole && (
          <MembershipsPanel
            workspaceId={workspaceId}
            renderSecondaryActions={
              isUserAdministrator
                ? member => (
                    <MemberActions
                      workspaceId={workspaceId}
                      member={member}
                      billingStatus={billingStatus}
                      customerId={customerId}
                      seats={seats}
                      plan={currentPlan!}
                      paymentDetails={paymentDetails}
                      isSsoEnabled={isSsoEnabled}
                    />
                  )
                : undefined
            }
          />
        )}
      </Section>

      {/* "data-testid="danger-zone-panel"" is used to keep consistency with the older panels in tests */}
      {isUserAdministrator && (
        <Section
          data-testid="danger-zone-panel"
          title="Delete Workspace"
          text="Delete the Workspace, including all documents and members it contains. Proceed with caution."
          action={
            <Button
              onClick={() =>
                showModal(DeleteWorkspaceModal, { id: workspaceId, name })
              }
              tiny
              variant="negative-secondary">
              Delete Workspace…
            </Button>
          }
        />
      )}
    </>
  )
}

export default WorkspaceSettingsGeneral
